// Bank statements: what every importer (OFX, CSV, later a CDR feed) produces, and the validation
// gate a statement must pass before any line reaches reconciliation (BRIEF.md section 8).

export interface StatementLine {
  /** Posted date, YYYY-MM-DD. */
  readonly date: string;
  readonly effectiveDate?: string;
  /** Signed minor units: money in positive, money out negative. */
  readonly amount: bigint;
  readonly description: string;
  /** Bank's running balance after this line, when the format has one. */
  readonly balance?: bigint;
  /** OFX transaction id. Not trusted across files: some banks restart it at 1 every export. */
  readonly fitid?: string;
  /** Evidence parsed from the description, e.g. original foreign amount and country. */
  readonly evidence?: Readonly<Record<string, string>>;
}

export interface ParsedStatement {
  readonly format: 'ofx' | 'csv';
  readonly currency?: string;
  readonly accountNumber?: string;
  /** Statement period from the file, if it states one. */
  readonly periodStart?: string;
  readonly periodEnd?: string;
  /** Opening and closing from the file, if it states or implies them. */
  readonly opening?: bigint;
  readonly closing?: bigint;
  /** Chronological: oldest first. */
  readonly lines: readonly StatementLine[];
}

export interface GateInput {
  readonly statement: ParsedStatement;
  /** Opening entered from the statement (first import of an OFX file). */
  readonly enteredOpening?: bigint;
  /** Closing of the previous import for this bank account, if there is one. */
  readonly previousClosing?: bigint;
  readonly previousPeriodEnd?: string;
}

export interface GateResult {
  readonly ok: boolean;
  /** Plain-language problems; empty when ok. */
  readonly problems: string[];
  readonly opening?: bigint;
  readonly closing?: bigint;
  readonly periodStart?: string;
  readonly periodEnd?: string;
  readonly openingSource?: 'previous' | 'file' | 'entered';
  readonly lines: readonly (StatementLine & { readonly dedupeKey: string })[];
}

export function formatCents(v: bigint): string {
  const neg = v < 0n;
  const abs = (neg ? -v : v).toString().padStart(3, '0');
  return `${neg ? '-' : ''}${abs.slice(0, -2)}.${abs.slice(-2)}`;
}

export function normaliseDescription(d: string): string {
  return d
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, ' ')
    .trim();
}

/**
 * Key for duplicate detection: date, amount, normalised description and the occurrence of that
 * combination within the day. Two identical card payments on one day stay two lines.
 */
export function withDedupeKeys<T extends StatementLine>(
  lines: readonly T[],
): (T & { dedupeKey: string })[] {
  const seen = new Map<string, number>();
  return lines.map((l) => {
    const base = `${l.date}|${l.amount}|${normaliseDescription(l.description)}`;
    const n = (seen.get(base) ?? 0) + 1;
    seen.set(base, n);
    return { ...l, dedupeKey: `${base}|${n}` };
  });
}

/** The four checks of BRIEF.md section 8. All must pass; nothing is ever partially imported. */
export function runGate(input: GateInput): GateResult {
  const { statement } = input;
  const problems: string[] = [];
  const lines = withDedupeKeys(statement.lines);
  if (lines.length === 0) problems.push('This file has no transactions in it.');

  // Opening: the previous import's closing is authoritative; otherwise the file, otherwise entered.
  let opening: bigint | undefined;
  let openingSource: GateResult['openingSource'];
  if (input.previousClosing !== undefined) {
    opening = input.previousClosing;
    openingSource = 'previous';
    const stated = statement.opening ?? input.enteredOpening;
    if (stated !== undefined && stated !== input.previousClosing) {
      problems.push(
        `This statement opens at ${formatCents(stated)}, but the last import closed at ${formatCents(input.previousClosing)}. A statement seems to be missing in between.`,
      );
    }
  } else if (statement.opening !== undefined) {
    opening = statement.opening;
    openingSource = 'file';
  } else if (input.enteredOpening !== undefined) {
    opening = input.enteredOpening;
    openingSource = 'entered';
  } else {
    problems.push('Enter the opening balance shown on the bank statement for this period.');
  }

  // Closing must be stated by the bank, never derived from the lines (that would prove nothing).
  const closing = statement.closing;
  if (closing === undefined) problems.push('The file does not state a closing balance.');

  // 1. Opening plus lines equals closing, to the cent.
  const total = lines.reduce((s, l) => s + l.amount, 0n);
  if (opening !== undefined && closing !== undefined && opening + total !== closing) {
    problems.push(
      `Opening ${formatCents(opening)} plus the transactions (${formatCents(total)}) gives ${formatCents(opening + total)}, but the statement closes at ${formatCents(closing)}. Some transactions are missing or extra.`,
    );
  }

  // Running balance, when the file has one: every non-zero line must follow from the one before.
  // Zero-amount lines (e.g. a declined card) can carry a balance that does not chain; they are skipped.
  if (opening !== undefined && lines.some((l) => l.balance !== undefined)) {
    let running = opening;
    for (const l of lines) {
      if (l.amount === 0n) continue;
      running += l.amount;
      if (l.balance !== undefined && l.balance !== running) {
        problems.push(
          `The running balance breaks on ${l.date} at "${l.description}": expected ${formatCents(running)}, the bank shows ${formatCents(l.balance)}.`,
        );
        break;
      }
    }
  }

  // 4. Every line inside the statement period.
  const dates = lines.map((l) => l.date).sort();
  const periodStart = statement.periodStart ?? dates[0];
  const periodEnd = statement.periodEnd ?? dates[dates.length - 1];
  const outside = lines.find(
    (l) => (periodStart && l.date < periodStart) || (periodEnd && l.date > periodEnd),
  );
  if (outside) {
    problems.push(
      `"${outside.description}" on ${outside.date} is outside the statement period ${periodStart} to ${periodEnd}.`,
    );
  }
  if (input.previousPeriodEnd && periodStart && periodStart <= input.previousPeriodEnd) {
    problems.push(
      `This statement overlaps one already imported (up to ${input.previousPeriodEnd}).`,
    );
  }

  return {
    ok: problems.length === 0,
    problems,
    ...(opening !== undefined ? { opening } : {}),
    ...(closing !== undefined ? { closing } : {}),
    ...(periodStart ? { periodStart } : {}),
    ...(periodEnd ? { periodEnd } : {}),
    ...(openingSource ? { openingSource } : {}),
    lines,
  };
}

/** Bank Australia (and others) put the original foreign amount in the description. */
export function foreignEvidence(description: string): Record<string, string> | undefined {
  const m = /([A-Z]{2})\s*FRGN AMT\s*-?\s*([\d.]+)/.exec(description);
  if (!m) return undefined;
  return { foreignCountry: m[1] as string, foreignAmount: m[2] as string };
}
